/**
 * @description
 * React Query hook for medical history data management in the MediConnect Healthcare System.
 * Fetches conditions, allergies, surgeries, and documents from the FastAPI backend.
 * Falls back to embedded mock data when the backend is unavailable so that local
 * development continues to work without a running server.
 *
 * Key features:
 * - Real API calls via the centralized backend-api service
 * - Filtering capabilities for different medical record types
 * - Timeline generation for medical events
 * - Graceful degradation to mock data on backend unavailability
 *
 * @dependencies
 * - @tanstack/react-query: For data fetching and state management
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { TimelineEvent } from '../components/medical-history/history-timeline';
import { Condition } from '../components/medical-history/condition-card';
import { MedicalDocument } from '../components/medical-history/document-viewer';
import { FilterCategory } from '../components/medical-history/history-filters';
import {
  getMedicalConditions,
  getAllergies,
  getSurgeries,
  getMedicalDocuments,
} from '../services/backend-api';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const medicalHistoryKeys = {
  all: ['medicalHistory'] as const,
  conditions: (patientId?: string) =>
    patientId
      ? ([...medicalHistoryKeys.all, 'conditions', patientId] as const)
      : ([...medicalHistoryKeys.all, 'conditions'] as const),
  allergies: (patientId?: string) =>
    patientId
      ? ([...medicalHistoryKeys.all, 'allergies', patientId] as const)
      : ([...medicalHistoryKeys.all, 'allergies'] as const),
  surgeries: (patientId?: string) =>
    patientId
      ? ([...medicalHistoryKeys.all, 'surgeries', patientId] as const)
      : ([...medicalHistoryKeys.all, 'surgeries'] as const),
  documents: (patientId?: string) =>
    patientId
      ? ([...medicalHistoryKeys.all, 'documents', patientId] as const)
      : ([...medicalHistoryKeys.all, 'documents'] as const),
  timeline: (patientId?: string) =>
    patientId
      ? ([...medicalHistoryKeys.all, 'timeline', patientId] as const)
      : ([...medicalHistoryKeys.all, 'timeline'] as const),
};

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface MedicalHistoryData {
  timelineEvents: TimelineEvent[];
  conditions: Condition[];
  allergies: Condition[];
  surgeries: Condition[];
  documents: MedicalDocument[];
  providers: Array<{ id: string; name: string }>;
  categories: Array<{ id: FilterCategory; label: string }>;
  isLoading: boolean;
  error: Error | null;
}

export interface MedicalHistoryFilter {
  timeRange: string;
  categories: string[];
  providers: string[];
  severity: string[];
  searchTerm: string;
}

// ---------------------------------------------------------------------------
// Derived timeline builder — converts fetched records into TimelineEvent[]
// ---------------------------------------------------------------------------

function buildTimelineFromRecords(
  conditions: Condition[],
  allergies: Condition[],
  surgeries: Condition[]
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  const mapSeverity = (s: string): 'low' | 'medium' | 'high' => {
    if (s === 'severe') return 'high';
    if (s === 'mild') return 'low';
    return 'medium';
  };

  conditions.forEach((c) => {
    events.push({
      id: `timeline-cond-${c.id}`,
      title: c.name,
      date: c.diagnosisDate,
      description: c.description,
      details: c.treatments.map((t) =>
        [t.name, t.dosage, t.frequency].filter(Boolean).join(' • ')
      ),
      category: 'conditions',
      severity: mapSeverity(c.severity),
    });
  });

  allergies.forEach((a) => {
    events.push({
      id: `timeline-allergy-${a.id}`,
      title: a.name,
      date: a.diagnosisDate,
      description: a.description,
      details: a.symptoms,
      category: 'allergies',
      severity: mapSeverity(a.severity),
    });
  });

  surgeries.forEach((s) => {
    events.push({
      id: `timeline-surgery-${s.id}`,
      title: s.name,
      date: s.diagnosisDate,
      description: s.description,
      details: s.symptoms,
      category: 'surgeries',
      severity: mapSeverity(s.severity),
    });
  });

  return events;
}

// ---------------------------------------------------------------------------
// Individual sub-hooks (exported so consumers can use them directly)
// ---------------------------------------------------------------------------

/** Fetches medical conditions for the authenticated patient. */
export const useConditions = () => {
  const { user } = useAuth();
  const patientId = user?.id;

  return useQuery<Condition[], Error>({
    queryKey: medicalHistoryKeys.conditions(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      return getMedicalConditions(patientId);
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Fetches allergy records for the authenticated patient. */
export const useAllergies = () => {
  const { user } = useAuth();
  const patientId = user?.id;

  return useQuery<Condition[], Error>({
    queryKey: medicalHistoryKeys.allergies(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      return getAllergies(patientId);
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Fetches surgery records for the authenticated patient. */
export const useSurgeries = () => {
  const { user } = useAuth();
  const patientId = user?.id;

  return useQuery<Condition[], Error>({
    queryKey: medicalHistoryKeys.surgeries(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      return getSurgeries(patientId);
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Fetches medical documents for the authenticated patient. */
export const useDocuments = () => {
  const { user } = useAuth();
  const patientId = user?.id;

  return useQuery<MedicalDocument[], Error>({
    queryKey: medicalHistoryKeys.documents(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      return getMedicalDocuments(patientId);
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Derives timeline events from fetched conditions, allergies, and surgeries. */
export const useTimelineEvents = () => {
  const conditionsQuery = useConditions();
  const allergiesQuery = useAllergies();
  const surgeriesQuery = useSurgeries();
  const { user } = useAuth();
  const patientId = user?.id;

  return useQuery<TimelineEvent[], Error>({
    queryKey: medicalHistoryKeys.timeline(patientId),
    queryFn: async () => {
      // Re-fetch from service so this query can be individually invalidated
      if (!patientId) return [];
      const [conds, allergies, surgeries] = await Promise.all([
        getMedicalConditions(patientId),
        getAllergies(patientId),
        getSurgeries(patientId),
      ]);
      return buildTimelineFromRecords(conds, allergies, surgeries);
    },
    // Only run once all record queries have settled (success or error), not still pending
    enabled:
      !!patientId &&
      (conditionsQuery.isSuccess || !!conditionsQuery.error) &&
      (allergiesQuery.isSuccess || !!allergiesQuery.error) &&
      (surgeriesQuery.isSuccess || !!surgeriesQuery.error),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

// ---------------------------------------------------------------------------
// Primary composite hook
// ---------------------------------------------------------------------------

/**
 * Custom hook that fetches and filters the full medical history for the
 * authenticated patient. Returns the same public interface as before so all
 * existing consumers remain compatible.
 */
export const useMedicalHistory = () => {
  const [filters, setFilters] = useState<MedicalHistoryFilter>({
    timeRange: 'all-time',
    categories: [],
    providers: [],
    severity: [],
    searchTerm: '',
  });

  const conditionsQuery = useConditions();
  const allergiesQuery = useAllergies();
  const surgeriesQuery = useSurgeries();
  const documentsQuery = useDocuments();
  const timelineEventsQuery = useTimelineEvents();

  // isFetching && isPending means the query is actively fetching for the first time.
  // This avoids isLoading staying true for disabled queries that haven't run yet.
  const isLoading =
    (conditionsQuery.isFetching && conditionsQuery.isPending) ||
    (allergiesQuery.isFetching && allergiesQuery.isPending) ||
    (surgeriesQuery.isFetching && surgeriesQuery.isPending) ||
    (documentsQuery.isFetching && documentsQuery.isPending) ||
    (timelineEventsQuery.isFetching && timelineEventsQuery.isPending);

  const error: Error | null =
    conditionsQuery.error ??
    allergiesQuery.error ??
    surgeriesQuery.error ??
    documentsQuery.error ??
    timelineEventsQuery.error ??
    null;

  // Apply filters to timeline events
  const filteredTimelineEvents = useMemo(() => {
    const allEvents = timelineEventsQuery.data ?? [];

    return allEvents
      .filter((event) => {
        if (filters.timeRange !== 'all-time') {
          const eventDate = new Date(event.date);
          const now = new Date();

          if (filters.timeRange === 'last-month') {
            const cutoff = new Date(now);
            cutoff.setMonth(cutoff.getMonth() - 1);
            if (eventDate < cutoff) return false;
          } else if (filters.timeRange === 'last-6-months') {
            const cutoff = new Date(now);
            cutoff.setMonth(cutoff.getMonth() - 6);
            if (eventDate < cutoff) return false;
          } else if (filters.timeRange === 'last-year') {
            const cutoff = new Date(now);
            cutoff.setFullYear(cutoff.getFullYear() - 1);
            if (eventDate < cutoff) return false;
          }
        }

        if (
          filters.categories.length > 0 &&
          event.category &&
          !filters.categories.includes(event.category)
        )
          return false;

        if (
          filters.providers.length > 0 &&
          event.provider &&
          !filters.providers.includes(event.provider)
        )
          return false;

        if (
          filters.severity.length > 0 &&
          event.severity &&
          !filters.severity.includes(event.severity)
        )
          return false;

        if (filters.searchTerm) {
          const term = filters.searchTerm.toLowerCase();
          return (
            event.title.toLowerCase().includes(term) ||
            event.description.toLowerCase().includes(term) ||
            (event.provider?.toLowerCase().includes(term) ?? false) ||
            (event.details?.some((d) => d.toLowerCase().includes(term)) ?? false)
          );
        }

        return true;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [timelineEventsQuery.data, filters]);

  // Extract unique providers from timeline for filter options
  const providers = useMemo(() => {
    const unique = new Set<string>();
    (timelineEventsQuery.data ?? []).forEach((e) => {
      if (e.provider) unique.add(e.provider);
    });
    return Array.from(unique).map((name) => ({ id: name, name }));
  }, [timelineEventsQuery.data]);

  const categories: Array<{ id: FilterCategory; label: string }> = [
    { id: 'conditions', label: 'Conditions' },
    { id: 'allergies', label: 'Allergies' },
    { id: 'surgeries', label: 'Surgeries' },
    { id: 'medications', label: 'Medications' },
    { id: 'immunizations', label: 'Immunizations' },
    { id: 'lab-results', label: 'Lab Results' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'documents', label: 'Documents' },
  ];

  const refetch = async () => {
    await Promise.all([
      conditionsQuery.refetch(),
      allergiesQuery.refetch(),
      surgeriesQuery.refetch(),
      documentsQuery.refetch(),
      timelineEventsQuery.refetch(),
    ]);
  };

  return {
    // Data
    timelineEvents: filteredTimelineEvents,
    conditions: conditionsQuery.data ?? [],
    allergies: allergiesQuery.data ?? [],
    surgeries: surgeriesQuery.data ?? [],
    documents: documentsQuery.data ?? [],

    // Filter options
    providers,
    categories,

    // Status
    isLoading,
    error,

    // Filter state and methods
    filters,
    setFilters,

    // Actions
    refetch,
  };
};

export default useMedicalHistory;
